import axios from "axios";
import type { RequestError } from "./schema";

/**
 * Collapses dynamic path segments (numeric ids, uuids) so requests to the
 * same endpoint template group together in Sentry regardless of which
 * record they touched, e.g. `/users/123/orders` -> `/users/:id/orders`.
 */
export function normalizeUrlPath(url?: string): string {
	if (!url) return "unknown-url";
	const [path] = url.split("?");
	return path
		.replace(/\/[0-9a-fA-F-]{8,}(?=\/|$)/g, "/:id")
		.replace(/\/\d+(?=\/|$)/g, "/:id");
}

export interface ApiRequestErrorOptions {
	status?: number;
	method?: string;
	url?: string;
	details: unknown;
	originalError?: unknown;
}

/**
 * Error thrown into Sentry for failed API requests. The message embeds the
 * endpoint, status, and backend message, so the Sentry issue title reads
 * e.g. "ApiRequestError: POST /payments/charge -> 401: Token expired"
 * instead of every request collapsing into "AxiosError: Request failed
 * with status code 401".
 */
export class ApiRequestError extends Error {
	readonly status?: number;
	readonly method?: string;
	readonly url?: string;
	readonly details: unknown;
	readonly originalError?: unknown;

	constructor(message: string, options: ApiRequestErrorOptions) {
		super(message);
		this.name = "ApiRequestError";
		this.status = options.status;
		this.method = options.method?.toUpperCase();
		this.url = options.url;
		this.details = options.details;
		this.originalError = options.originalError;
		Object.setPrototypeOf(this, ApiRequestError.prototype);
	}
}

/**
 * Builds a Sentry-friendly error out of a raw axios rejection plus the
 * already-parsed backend error body.
 */
export function toApiRequestError(
	error: unknown,
	parsed: RequestError,
): ApiRequestError {
	const axiosErr = axios.isAxiosError(error) ? error : undefined;
	const status = axiosErr?.response?.status;
	const method = axiosErr?.config?.method;
	const url = axiosErr?.config?.url;

	const statusPart = status ? String(status) : axiosErr?.code ?? "NO_RESPONSE";
	const message = `${method ? method.toUpperCase() : "?"} ${
		url ?? "unknown-url"
	} \u2192 ${statusPart}: ${parsed.message}`;

	return new ApiRequestError(message, {
		status,
		method,
		url,
		details: parsed.details,
		originalError: error,
	});
}

/**
 * Explicit Sentry grouping key: same endpoint template + method + status =
 * same issue, distinct combinations = distinct issues. Without this,
 * Sentry groups by exception type + message, and axios's generic message
 * ("Request failed with status code 401") would still lump unrelated
 * endpoints together.
 */
export function apiErrorFingerprint(err: ApiRequestError): string[] {
	return [
		"api-error",
		err.method ?? "unknown-method",
		normalizeUrlPath(err.url),
		String(err.status ?? "no-status"),
	];
}
